'use client';

import './header.scss';

import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce, throttle } from 'throttle-debounce';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { LogoChooser } from '@sola/ark-core/atoms/logos/logos.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { getPublicConfig } from '@sola/config/lib/getters.js';
import { LogoName } from '@sola/schema/lib/logos.js';
import { Link as LinkProps } from '@sola/schema/lib/schema.js';
import { HeaderProps } from '@sola/schema/lib/skippy.js';
import { trackClick } from '@sola/tracking/components/datalayer.js';

import { useNavigationHover } from '../../hooks/use-navigation-hover.js';
import { NavAccount } from '../../molecules/nav-account/nav-account.js';
import { NavBag } from '../../molecules/nav-bag/nav-bag.jsx';
import { NavSearch } from '../../molecules/nav-search/nav-search.jsx';
import { NavSheet } from '../../molecules/nav-sheet/nav-sheet.jsx';
import { NavLevel, NavStack } from '../../molecules/nav-stack/nav-stack.jsx';
import { getMenuItemColumns } from '../../utils/nav-utils.js';

type CustomizeProps = {
  logo?: LogoName;
  link?: LinkProps;
  showShoppingBag?: boolean;
  showLogin?: boolean;
  showSearch?: boolean;
  experiments?: string[];
  urlLogin?: string;
  urlAccountHome?: string;
  userMenu?: React.ReactNode;
};

export const bodyScrollLock = (
  isLocked: boolean,
  lastScrollPosition: number,
) => {
  if (!document || !lastScrollPosition) {
    return;
  }
  const { body } = document;
  if (isLocked) {
    body.style.position = 'fixed';
    body.style.top = `-${lastScrollPosition}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    return;
  }

  body.style.position = '';
  body.style.top = '';
  body.style.width = '';
  body.style.overflow = '';
  window.scrollTo(0, lastScrollPosition);
};

export const Header: React.FC<HeaderProps & CustomizeProps> = ({
  primaryMenu,
  logo = 'compassion',
  link,
  showShoppingBag,
  showLogin,
  showSearch,
  experiments,
  urlLogin,
  urlAccountHome,
  userMenu,
}) => {
  const menuItems = primaryMenu.items || [];
  const { t } = useTranslation(['priority']);
  const [menuOpen, setMenuOpen] = useState('');
  const isMobileMenuOpen = menuOpen === 'mobile';
  const [navStack, setNavStack] = useState<NavLevel[]>([{ items: menuItems }]);
  const pathname = usePathname();
  const [headerHidden, setHeaderHidden] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTopRef = useRef(0);
  const mobileCloseButtonRef = useRef<HTMLButtonElement>(null);
  const mobileOpenButtonRef = useRef<HTMLButtonElement>(null);
  const { NEXT_PUBLIC_BASE_URL_MYACCOUNT } = getPublicConfig();

  const {
    activeItem,
    setActiveItem,
    handleTriggerEnter,
    handleTriggerLeave,
    handleContentEnter,
    handleContentLeave,
    handleViewportEnter,
    handleViewportLeave,
    resetNavigation,
  } = useNavigationHover({
    switchDelay: 200,
    exitDelay: 400,
  });

  const throttledHandleScroll = useMemo(
    () =>
      throttle(250, () => {
        if (menuOpen === 'mobile' || activeItem) {
          return;
        }

        resetNavigation();

        const reappearTimeout = 5000; // 5 seconds
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;

        // check > 80 to prevent whitespace behind the navbar appearing and mitigate rubberbanding on iOS
        if (scrollTop > lastScrollTopRef.current && scrollTop > 80) {
          setHeaderHidden(true);
        } else {
          setHeaderHidden(false);
        }

        lastScrollTopRef.current = scrollTop;

        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
          setHeaderHidden(false);
        }, reappearTimeout);
      }),
    [menuOpen, activeItem, resetNavigation],
  );

  useEffect(() => {
    if (isMobileMenuOpen) {
      const debouncedFocus = debounce(100, () => {
        mobileCloseButtonRef.current?.focus();
      });
      debouncedFocus();
      bodyScrollLock(true, lastScrollTopRef.current);
      return;
    }
    const debouncedFocus = debounce(200, () => {
      mobileOpenButtonRef.current?.focus();
    });
    debouncedFocus();
    bodyScrollLock(false, lastScrollTopRef.current);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    window.addEventListener('scroll', throttledHandleScroll);

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current); // Cleanup timeout on unmount
      }
    };
  }, [throttledHandleScroll]);

  useEffect(() => {
    setMenuOpen('');
    resetNavigation();
  }, [pathname, resetNavigation]);

  useEffect(() => {
    if (menuOpen === 'mobile') {
      return;
    }
    if (activeItem) {
      setMenuOpen(activeItem);
      return;
    }
    setMenuOpen('');
  }, [activeItem, menuOpen]);

  return (
    <header
      role="banner"
      className={cn('Header', {
        'Header--hidden': headerHidden,
      })}
    >
      <ButtonStyle>
        <a className="Header__skip-link" href="#main">
          {t('header.skipLink')}
        </a>
      </ButtonStyle>
      <div
        className={cn('Header__container', {
          'is-mobile-open': isMobileMenuOpen,
        })}
      >
        <div className="Header__menu-list-container w-full">
          <div className="Header__logo-container">
            <Link
              href={link?.link || '/'}
              title={link?.title || t('header.logoAlt')}
            >
              <LogoChooser
                name={logo}
                className="Header__logo"
                color="full"
                aria-labelledby="Header-logo-label"
                priority
              />
              <span id="Header-logo-label" className="sr-only">
                {t('header.logoAlt', { title: link?.title })}
              </span>
            </Link>
          </div>

          <NavigationMenu.Root
            aria-label="Main navigation"
            value={menuOpen}
            className="Header__root flex-col hidden lg:flex"
          >
            <NavigationMenu.List className="Header__menu-list" asChild>
              <div>
                {/* desktop menu */}
                <ul className="Header__desktop-items xl:mx-roomy-xs">
                  {menuItems.map((item) => {
                    const hasChildren = Boolean(item.items?.length);
                    const columns = getMenuItemColumns(item);
                    return (
                      <NavigationMenu.Item
                        value={item.title}
                        key={item.title}
                        className="flex justify-center"
                      >
                        <NavigationMenu.Trigger
                          onTouchEnd={(e) => e.preventDefault()}
                          onPointerEnter={() =>
                            item.title && handleTriggerEnter(item.title)
                          }
                          onPointerLeave={handleTriggerLeave}
                          onFocus={() => {
                            if (menuOpen && item.title && hasChildren) {
                              handleTriggerEnter(item.title);
                            }
                          }}
                          onBlur={() => {
                            if (menuOpen && hasChildren) {
                              handleTriggerLeave();
                            }
                          }}
                          onClick={(e) => {
                            if (!item.title) {
                              return;
                            }
                            trackClick(
                              item.title,
                              item?.link || '#',
                              'Header__trigger',
                            );
                            if (!hasChildren) {
                              return;
                            }
                            if (menuOpen && e.detail !== 0) {
                              e.preventDefault();
                            }
                            // actions for screenreaders (tabs)
                            if (menuOpen === item.title) {
                              setActiveItem('');
                            } else {
                              setActiveItem(item.title);
                            }
                          }}
                          className={cn(
                            'Header__trigger flex items-center justify-between',
                            {
                              'Header__trigger--active':
                                menuOpen === item.title,
                            },
                          )}
                          asChild
                        >
                          {hasChildren ? (
                            <button type="button">
                              <p className="label label-potent label-neutral flex items-center gap-snug-md">
                                {item.title}
                                <ArkIcon
                                  iconName="chevron-down"
                                  className="Header__chevron"
                                />
                              </p>
                            </button>
                          ) : (
                            <Link
                              href={item?.link || '#'}
                              title={item?.title}
                              target={item?.target}
                              className="Header__desktop-nav-item label label-potent label-neutral"
                            >
                              {item.title}
                            </Link>
                          )}
                        </NavigationMenu.Trigger>
                        {hasChildren && (
                          <NavigationMenu.Content
                            className="Header__content Header__container flex justify-center"
                            onPointerEnter={handleContentEnter}
                            onPointerLeave={handleContentLeave}
                            onFocus={handleContentEnter}
                            onBlur={handleContentLeave}
                          >
                            <div className="w-full max-w-xl flex flex-col">
                              <div className="pt-roomy-xs pb-comfy-lg">
                                <div className="Header__link-container pb-snug-md">
                                  <LinkStyle
                                    noPadding
                                    noUnderline
                                    size="lg"
                                    iconAfter="arrow-right"
                                  >
                                    <Link
                                      href={item?.link || '#'}
                                      target={item?.target}
                                      title={item?.title}
                                    >
                                      {item.title}
                                    </Link>
                                  </LinkStyle>
                                </div>
                              </div>
                              <div className="Header__navsheet-container gap-comfy-xl pb-comfy-xl pt-comfy-xs">
                                {columns.map((col, idx) => {
                                  return (
                                    <NavSheet
                                      key={col.map((ci) => ci.title).join()}
                                      items={col}
                                      {...(idx === 2 ? { cta: item.cta } : {})}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                          </NavigationMenu.Content>
                        )}
                      </NavigationMenu.Item>
                    );
                  })}
                </ul>
              </div>
            </NavigationMenu.List>

            {/* The entering animation seems to like having this here. Not sure why. */}
            <NavigationMenu.NavigationMenuIndicator />

            <div className="Header__viewport-position">
              <NavigationMenu.Viewport
                className="Header__menu-viewport flex justify-center"
                onPointerEnter={handleViewportEnter}
                onPointerLeave={handleViewportLeave}
                onFocus={handleViewportEnter}
                onBlur={handleViewportLeave}
              />
            </div>
          </NavigationMenu.Root>

          <NavigationMenu.Root
            onValueChange={(value) => {
              if (value === 'mobile') {
                setMenuOpen(value);
                resetNavigation();
              }
            }}
            value={menuOpen}
            className="Header__root flex flex-col"
            aria-label="Action navigation"
          >
            <NavigationMenu.List className="Header__menu-list" asChild>
              <div className="Header__desktop-actions flex items-center gap-comfy-sm justify-end">
                <div className="flex flex-row gap-snug-md md:gap-snug-xl lg:gap-comfy-sm">
                  <div className="flex gap-snug-md md:gap-snug-xl">
                    {showSearch && <NavSearch />}
                    {showShoppingBag && <NavBag />}
                  </div>
                  {showLogin && (
                    <NavAccount
                      urlLogin={urlLogin}
                      urlAccountHome={urlAccountHome}
                      userMenu={userMenu}
                    />
                  )}

                  {/* mobile menu */}
                  <NavigationMenu.Item
                    className="Header__nav-action Header__mobile-menu-trigger flex lg:hidden items-center"
                    key="mobile hamburger menu"
                    value="mobile"
                    asChild
                  >
                    <div>
                      <NavigationMenu.Trigger
                        onPointerEnter={(e) => e.stopPropagation()}
                        onPointerMove={(e) => e.preventDefault()}
                        onPointerLeave={(e) => e.preventDefault()}
                        onClick={() => {
                          trackClick(t('header.menu'));
                        }}
                        className="flex items-center p-comfy-xs rounded-full"
                        title={t('header.menu')}
                      >
                        <ArkIcon
                          iconName="menu-01"
                          className="Header__nav-icon"
                        />
                      </NavigationMenu.Trigger>
                      <NavigationMenu.Content
                        className="Header__content Header__content--mobile flex flex-col"
                        onPointerLeave={(e) => e.preventDefault()}
                      >
                        <div
                          className={cn(
                            'Header__mobile-nav flex px-comfy-xs py-comfy-lg items-center',
                            {
                              'justify-end': navStack.length === 1,
                              'justify-between': navStack.length > 1,
                            },
                          )}
                        >
                          {Boolean(navStack.length > 1) && (
                            <button
                              ref={mobileOpenButtonRef}
                              className="flex items-center"
                              type="button"
                              onClick={() => {
                                setNavStack((prev) => prev.slice(0, -1));
                              }}
                            >
                              <ArkIcon
                                iconName="arrow-left"
                                className="Header__nav-icon"
                              />
                            </button>
                          )}

                          <span className="flex items-center gap-snug-md md:gap-snug-xl lg:gap-comfy-sm">
                            {showSearch && <NavSearch />}
                            {showShoppingBag && <NavBag />}
                            {showLogin && (
                              <NavAccount
                                urlLogin={urlLogin}
                                urlAccountHome={urlAccountHome}
                                userMenu={userMenu}
                              />
                            )}
                            <button
                              ref={mobileCloseButtonRef}
                              className="Header__nav-action flex items-center p-comfy-xs"
                              type="button"
                              title={t('header.menu')}
                              onClick={() => {
                                setMenuOpen('');
                                setNavStack([{ items: menuItems }]);
                                resetNavigation();
                              }}
                            >
                              <ArkIcon
                                iconName="x-close"
                                className="Header__nav-icon"
                              />
                            </button>
                          </span>
                        </div>
                        <NavStack
                          stack={navStack}
                          setStack={setNavStack}
                          showMyAccountSection={showLogin}
                          className="pb-roomy-sm"
                        />
                      </NavigationMenu.Content>
                    </div>
                  </NavigationMenu.Item>
                </div>
              </div>
            </NavigationMenu.List>

            {/* The entering animation seems to like having this here. Not sure why. */}
            <NavigationMenu.NavigationMenuIndicator />

            <div className="Header__viewport-position">
              <NavigationMenu.Viewport
                className="Header__menu-viewport flex justify-center"
                onPointerEnter={handleViewportEnter}
                onPointerLeave={handleViewportLeave}
              />
            </div>
          </NavigationMenu.Root>
        </div>
      </div>
    </header>
  );
};

export default Header;
